import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Countdown from 'react-countdown';

import InformationSubMenu from '@/components/nav/InformationSubMenu';
import ComingSoonPopup from '@/components/ui/ComingSoonPopup';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import { getLastDateOfTheMonth } from '@/utils/utils';
// import type { LayoutWrapperContents } from '@/types/LayoutWrapperContents';

const Index = () => {
  const [open, setOpen] = useState(false);
  // const layoutWrapperContents: LayoutWrapperContents = {
  //   Heading: `Filing for the 2024 tax year is now open for <span style="color:#1da94a; font-weight: 600;">W-2, 1099, and 1098 forms</span>.`,
  //   SubHeading: 'Don’t wait—file today!',
  //   CtaText: 'Start Filing ',
  //   CtaSubHeading: ``,
  //   CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w21099forms`,
  //   isActive: true,
  // };

  const [lastDayTaxSeason, setLastDayTaxSeason] = useState(new Date());

  useEffect(() => {
    setLastDayTaxSeason(getLastDateOfTheMonth(new Date().getFullYear(), 3, 3));
    setInterval(() => {
      setLastDayTaxSeason(
        getLastDateOfTheMonth(new Date().getFullYear(), 3, 31)
      );
    }, 1000); // Updates every second (adjust if needed)
  }, []);

  // Random component
  const Completionist = () => <span />;

  // Renderer callback with condition
  const renderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
  }: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) {
      // Render a completed state
      return <Completionist />;
    }
    // Render a countdown
    return (
      <Stack
        direction="row"
        justifyContent={{ xs: 'center', md: 'flex-start' }}
        alignItems="center"
        spacing={1}
        sx={{
          '& .timer-count': {
            border: '1px solid #848484',
            p: 1.5,
            textAlign: 'center',
            minWidth: { xs: 55, sm: 70, md: 70 },
            position: 'relative',
            borderRadius: 2,
            boxShadow: ' 0 0 20px rgba(0,0,0,.12)',
          },
          '& .timer-number': {
            fontSize: { xs: 18, sm: 24 },
            fontWeight: 900,
          },
          '& .colon': {
            fontSize: { xs: 18, sm: 24 },
            px: { xs: 0.2, sm: 0.5 },
            py: 0,
            mt: { xs: '-8px !important', sm: '-14px !important' },
          },
          '& .timer-label': {
            background: '#000',
            borderTop: '1px solid #848484',
            fontSize: { xs: 10, sm: 12 },
            fontWeight: 500,
            mt: 0,
            mx: -1.5,
            mb: -1.5,
            py: 0.2,
            px: { xs: 0.5, sm: 1, lg: 0.5 },
            borderRadius: '0px 0px 8px 8px',
          },
        }}
      >
        <Box className="timer-count">
          <Box className="timer-number">
            {days?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Days</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {hours?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Hours</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {minutes?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Minutes</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {seconds?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Seconds</Box>
        </Box>
      </Stack>
    );
  };

  return (
    <AppLayout
      // LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          title="IRS Information Returns: e-Filing 1099 and W-2 Forms"
          description="Explore IRS information returns, which include Form 1099 and W-2, to ensure accurate income reporting, tax compliance, and essential tax-filing requirements."
          keywords="information returns, 1099 forms, W-2 Form, Wage Reporting"
          canonical="information-returns/"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
          ogShareImg="assets/images/information-share-img.png"
        />
      }
    >
      <InformationSubMenu />
      <ComingSoonPopup open={open} setOpen={setOpen} />
      <Container
        sx={{
          position: 'relative',
          mt: 2,
          mb: 0,
        }}
        maxWidth="lg"
      >
        <Box
          sx={{
            background:
              'linear-gradient(275deg,rgba(10, 87, 71, 1) 0%, rgba(19, 32, 172, 1) 100%)',
            p: 1.5,
            px: 2.5,
            color: '#fff',
          }}
          className="ribbon"
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 1, md: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box sx={{ ml: 0.8 }}>
              <Typography
                component="div"
                variant="body1"
                sx={{
                  textAlign: { xs: 'left', sm: 'left' },
                  fontSize: { xs: 16, sm: 17, md: 18, lg: '1.20rem' },
                  color: '#eaeaeaff',
                  lineHeight: 1.5,

                  '& span': { fontWeight: 600, color: '#ddd' },
                }}
              >
                <strong style={{ fontWeight: 700 }}>
                  📢 Deadline Has Passed!
                </strong>{' '}
                E-File{' '}
                <b style={{ color: '#ffe6a7', fontWeight: 600 }}>
                  {' '}
                  Form 5498 series{' '}
                </b>
                today to report your{' '}
                <b style={{ color: '#ffe6a7', fontWeight: 600 }}>
                  2025 IRA, HSA & ESA
                </b>{' '}
                contributions and avoid penalties.{' '}
              </Typography>
            </Box>
            <Box>
              {/* <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mb: { xs: 1, sm: 1 },
                  lineHeight: 1.3,
                }}
              >
                Time left to file before the deadline{' '}
                <Hidden smUp>
                  <br />
                </Hidden>
                (
                <span style={{ fontWeight: 600, color: '#fff' }}>
                  March 2, 2026
                </span>
                ):
              </Typography> */}
              <Countdown date={lastDayTaxSeason} renderer={renderer} />
            </Box>
            <Box>
              <Button
                variant="contained"
                size="small"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=informationreturns`}
                sx={{
                  backgroundColor: '#fff',
                  '&:hover, &:focus': {
                    backgroundColor: '#213e99',
                    color: '#fff',
                  },
                  px: 2,
                  color: '#333',
                  mt: { xs: 0, md: 0 },
                  display: { xs: 'inline-block', sm: 'inline-block' },
                  mx: { xs: 'auto', sm: 1 },
                  borderRadius: 3,
                  boxShadow: 'none',
                  fontSize: { xs: 14, sm: 16, md: 18 },
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                E-file Now
              </Button>
            </Box>
          </Stack>
        </Box>
      </Container>

      <Box sx={{ maxWidth: 1920, mx: 'auto' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                E-File IRS{' '}
                <span className="highlight-container">
                  <span className="highlight">Information Returns</span>
                </span>{' '}
                with Ease for{' '}
                <span style={{ fontWeight: 700 }}>Tax Year 2025!</span>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                Filing information returns such as W-2s, 1099s, and 1098s can be
                tedious and time-consuming. TaxZerone simplifies the process,
                allowing you to submit these forms electronically to the IRS in
                just minutes.
              </Typography>
              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Button
                  variant="contained"
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                  sx={{
                    mr: { xs: 0, md: 4 },
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                >
                  Start Filing Now
                </Button>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      mb: { xs: '0 !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/irs-authorized.webp" />
                      <source srcSet="/assets/images/irs-authorized.png" />
                      <Box
                        component="img"
                        src="/assets/images/irs-authorized.png"
                        loading="lazy"
                        alt="IRS-authorized e-file provider"
                        width={70}
                        height={69}
                        sx={{
                          width: { xs: 50, md: 70 },
                          height: { xs: 49, md: 69 },
                        }}
                      />
                    </picture>
                  </Box>
                  <Box
                    sx={{
                      mb: { xs: '0px !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/tzo-soc2.webp" />
                      <source srcSet="/assets/images/tzo-soc2.png" />
                      <Box
                        component="img"
                        src="/assets/images/tzo-soc2.png"
                        loading="lazy"
                        alt="TZO SOC2 Compliant"
                        width={90}
                        height={89}
                        sx={{
                          width: { xs: 70, md: 90 },
                          height: { xs: 69, md: 89 },
                        }}
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>

              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={0}
                m={0}
                mt={{ xs: 2, md: 5 }}
                pb={0}
                textAlign="center"
                sx={{ ml: { xs: 0, md: -5 } }}
              >
                <Grid size={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      borderWidth: 0,
                      fontSize: { xs: 9, sm: 12, lg: 13 },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: '#efefef',
                        borderRadius: '50%',
                        width: { xs: 40, sm: 70 },
                        height: { xs: 40, sm: 70 },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mx: 'auto',
                        mb: { xs: 0.5, sm: 1 },
                        p: { xs: 1, sm: 2 },
                        textAlign: 'center',
                      }}
                    >
                      <picture style={{ width: '100%', height: '100%' }}>
                        <source
                          srcSet="/assets/images/secure-new-icon.webp"
                          width="100%"
                          height="100%"
                        />
                        <source
                          srcSet="/assets/images/secure-new-icon.png"
                          width="100%"
                          height="100%"
                        />
                        <img
                          src="/assets/images/secure-new-icon.png"
                          alt="Safe & Secure"
                          loading="lazy"
                          width="100%"
                          height="100%"
                        />
                      </picture>
                    </Box>
                    <strong>
                      Safe &
                      <Hidden lgUp mdDown>
                        <br />
                      </Hidden>{' '}
                      Secure
                    </strong>
                  </Paper>
                </Grid>
                <Grid size={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      borderWidth: 0,
                      fontSize: { xs: 9, sm: 12, lg: 13 },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: '#efefef',
                        borderRadius: '50%',
                        width: { xs: 40, sm: 70 },
                        height: { xs: 40, sm: 70 },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mx: 'auto',
                        mb: { xs: 0.5, sm: 1 },
                        p: { xs: 1, sm: 2 },
                        textAlign: 'center',
                      }}
                    >
                      <picture style={{ width: '100%', height: '100%' }}>
                        <source
                          srcSet="/assets/images/irs-new-icon.webp"
                          width="100%"
                          height="100%"
                        />
                        <source
                          srcSet="/assets/images/irs-new-icon.png"
                          width="100%"
                          height="100%"
                        />
                        <img
                          src="/assets/images/irs-new-icon.png"
                          alt="IRS Authorized"
                          loading="lazy"
                          width="100%"
                          height="100%"
                        />
                      </picture>
                    </Box>
                    <strong>
                      IRS
                      <Hidden lgUp mdDown>
                        <br />
                      </Hidden>{' '}
                      Authorized
                    </strong>
                  </Paper>
                </Grid>
                <Grid size={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 1, sm: 2 },
                      borderRadius: 0,
                      borderWidth: 0,
                      fontSize: { xs: 9, sm: 12, lg: 13 },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: '#efefef',
                        borderRadius: '50%',
                        width: { xs: 40, sm: 70 },
                        height: { xs: 40, sm: 70 },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mx: 'auto',
                        mb: { xs: 0.5, sm: 1 },
                        p: { xs: 1, sm: 2 },
                        textAlign: 'center',
                      }}
                    >
                      <picture style={{ width: '100%', height: '100%' }}>
                        <source
                          srcSet="/assets/images/lowest-new-icon.webp"
                          width="100%"
                          height="100%"
                        />
                        <source
                          srcSet="/assets/images/lowest-new-icon.png"
                          width="100%"
                          height="100%"
                        />
                        <img
                          src="/assets/images/lowest-new-icon.png"
                          alt="Affordable Price"
                          loading="lazy"
                          width="100%"
                          height="100%"
                        />
                      </picture>
                    </Box>
                    <strong>
                      Affordable
                      <Hidden lgUp mdDown>
                        <br />
                      </Hidden>{' '}
                      Filing Fee
                    </strong>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Hidden lgDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
              className="information-bg"
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/information-img.webp"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <source
                  srcSet="/assets/images/information-img.png"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <img
                  src="/assets/images/information-img.png"
                  alt="E-File IRS Information Returns Easily"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden lgUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
              className="information-bg"
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/information-img-sm.webp"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <source
                  srcSet="/assets/images/information-img-sm.png"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <img
                  src="/assets/images/information-img-sm.png"
                  alt="E-File IRS Information Returns Easily"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Box>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          overflow: 'hidden',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={2}
            m={2}
            mt={5}
            pb={5}
            mb={{ xs: 5, sm: 5 }}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                E-file Information Returns with TaxZerone
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Save time and effort, avoid costly mistakes, and stay secure and
                compliant
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form W-2
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Wage and Tax Statement - Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    wages and taxes withheld
                  </Link>{' '}
                  for employees.{' '}
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form W-2
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/w2-img.webp" />
                    <source srcSet="/assets/images/w2-img.png" />
                    <img
                      src="/assets/images/w2-img.png"
                      alt="Form W-2, Wage and Tax Statement"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form W-2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Corrected Wage and Tax Statement - Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    corrected wages and taxes
                  </Link>{' '}
                  withheld for employees.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form W-2c
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/w2c-img.webp" />
                    <source srcSet="/assets/images/w2c-img.png" />
                    <img
                      src="/assets/images/w2c-img.png"
                      alt="Form W-2c, Corrected Wage and Tax Statement"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-NEC
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 72 }}>
                  Nonemployee Compensation - Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-nec/"
                    sx={{ color: '#0202ff' }}
                  >
                    non-employee compensation
                  </Link>{' '}
                  reporting.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-NEC
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-nec-img.webp" />
                    <source srcSet="/assets/images/1099-nec-img.png" />
                    <img
                      src="/assets/images/1099-nec-img.png"
                      alt="Form 1099-NEC, Nonemployee Compensation"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-MISC
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Miscellaneous Information - Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-misc/"
                    sx={{ color: '#0202ff' }}
                  >
                    miscellaneous income
                  </Link>
                  , like payments to contractors, except compensation.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-MISC
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-misc-img.webp" />
                    <source srcSet="/assets/images/1099-misc-img.png" />
                    <img
                      src="/assets/images/1099-misc-img.png"
                      alt="Form 1099-MISC, Miscellaneous Information"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-INT
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Interest Income - Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-int/"
                    sx={{ color: '#0202ff' }}
                  >
                    Interest Income
                  </Link>{' '}
                  for whom withheld and paid any foreign tax on interest{' '}
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-INT
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-int-img.webp" />
                    <source srcSet="/assets/images/1099-int-img.png" />
                    <img
                      src="/assets/images/1099-int-img.png"
                      alt="Form 1099-INT, Interest Income"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-DIV
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Dividends and Distributions - Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-div/"
                    sx={{ color: '#0202ff' }}
                  >
                    dividends and other distributions
                  </Link>{' '}
                  to taxpayers.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-DIV
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-div-img.webp" />
                    <source srcSet="/assets/images/1099-div-img.png" />
                    <img
                      src="/assets/images/1099-div-img.png"
                      alt="Form 1099-DIV, Dividends and Distributions"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-A
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 96 }}>
                  {' '}
                  Acquisition or Abandonment of Secured Property - Report
                  information when property is acquired by a{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-a/"
                    sx={{ color: '#0202ff' }}
                  >
                    lender or abandoned by a borrower
                  </Link>
                  .
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-A
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-a-img.webp" />
                    <source srcSet="/assets/images/1099-a-img.png" />
                    <img
                      src="/assets/images/1099-a-img.png"
                      alt="Acquisition or Abandonment of Secured Property"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-B
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 96 }}>
                  Proceeds From Broker and Barter Exchange Transactions - Report
                  Proceeds from{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-b/"
                    sx={{ color: '#0202ff' }}
                  >
                    Broker and Barter Exchange Transactions
                  </Link>
                  .
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-B
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-b-img.webp" />
                    <source srcSet="/assets/images/1099-b-img.png" />
                    <img
                      src="/assets/images/1099-b-img.png"
                      alt="Form 1099-B, Proceeds From Broker and Barter Exchange Transactions"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-K
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Payment Card and Third Party Network Transactions - Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-k/"
                    sx={{ color: '#0202ff' }}
                  >
                    Payment Card and Third Party Network Transactions
                  </Link>
                  .
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 1,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-K
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-k-img.webp" />
                    <source srcSet="/assets/images/1099-k-img.png" />
                    <img
                      src="/assets/images/1099-k-img.png"
                      alt="Form 1099-K, Payment Card and Third Party Network Transactions"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-G
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 96 }}>
                  Certain Government Payments - Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-g/"
                    sx={{ color: '#0202ff' }}
                  >
                    Certain Government Payments
                  </Link>
                  .
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-G
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-g-img.webp" />
                    <source srcSet="/assets/images/1099-g-img.png" />
                    <img
                      src="/assets/images/1099-g-img.png"
                      alt="Form 1099-G, Certain Government Payments"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-R
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Distributions From Pensions, Annuities, Retirement or
                  Profit-Sharing Plans, IRAs, Insurance Contracts, etc. - Report
                  Distributions From Pensions, Annuities,{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-r/"
                    sx={{ color: '#0202ff' }}
                  >
                    Retirement or Profit-Sharing Plans
                  </Link>
                  , IRAs, Insurance Contracts, etc.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-R
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-r-img.webp" />
                    <source srcSet="/assets/images/1099-r-img.png" />
                    <img
                      src="/assets/images/1099-r-img.png"
                      alt="Form 1099-R, Distributions From Pensions, Annuities, Retirement or Profit-Sharing Plans, IRAs, Insurance Contracts, etc."
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-C
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1099-C, Cancellation of Debt - Report the debt for which
                  you{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    canceled $600
                  </Link>{' '}
                  or more owed to you.{' '}
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 6,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-C
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-c-img.webp" />
                    <source srcSet="/assets/images/1099-c-img.png" />
                    <img
                      src="/assets/images/1099-c-img.png"
                      alt="Form 1099-C, Cancellation of Debt"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-S
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1099-S, Proceeds From Real Estate Transactions - Report
                  the gross proceeds from a{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    real estate sale or exchange{' '}
                  </Link>
                  .{' '}
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 6,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-S
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-s-img.webp" />
                    <source srcSet="/assets/images/1099-s-img.png" />
                    <img
                      src="/assets/images/1099-s-img.png"
                      alt="Form 1099-S, Proceeds From Real Estate Transactions "
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-SA
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1099-SA, Distributions From an HSA, Archer MSA, or
                  Medicare Advantage MSA - Report distributions made from a{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-sa/"
                    sx={{ color: '#0202ff' }}
                  >
                    health savings account (HSA){' '}
                  </Link>
                  , Archer medical savings account (Archer MSA), or Medicare
                  Advantage MSA (MA MSA).
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 3,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-SA
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-sa-img.webp" />
                    <source srcSet="/assets/images/1099-sa-img.png" />
                    <img
                      src="/assets/images/1099-sa-img.png"
                      alt="Form 1099-S, Proceeds From Real Estate Transactions "
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-LTC
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1099-LTC, Long-Term Care and Accelerated Death Benefits -
                  Report long-term care and{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-ltc/"
                    sx={{ color: '#0202ff' }}
                  >
                    accelerated death benefits{' '}
                  </Link>
                  paid to policyholders.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 8,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-LTC
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-ltc-img.webp" />
                    <source srcSet="/assets/images/1099-ltc-img.png" />
                    <img
                      src="/assets/images/1099-ltc-img.png"
                      alt="Form 1099-LTC, Long-Term Care and Accelerated Death Benefits "
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-OID
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1099-OID, Original Issue Discount - RReport original
                  issue discount (OID) on certain{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-oid/"
                    sx={{ color: '#0202ff' }}
                  >
                    debt instruments{' '}
                  </Link>
                  sold at a discount to the face value.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 8,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-OID
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-oid-img.webp" />
                    <source srcSet="/assets/images/1099-oid-img.png" />
                    <img
                      src="/assets/images/1099-oid-img.png"
                      alt="Form 1099-S, Proceeds From Real Estate Transactions "
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-PATR
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1099-PATR, Taxable Distributions Received From
                  Cooperatives - Report taxable distributions of profits{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-patr/"
                    sx={{ color: '#0202ff' }}
                  >
                    patronage dividends{' '}
                  </Link>
                  and certain other income to the patrons.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 7,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-PATR
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-patr-img.webp" />
                    <source srcSet="/assets/images/1099-patr-img.png" />
                    <img
                      src="/assets/images/1099-patr-img.png"
                      alt="Form 1099-PATR, Taxable Distributions Received From Cooperatives"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-Q
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1099-Q, Payments From Qualified Education Programs (Under
                  Sections 529 and 530) -Report distributions made from
                  qualified education programs like{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-q/"
                    sx={{ color: '#0202ff' }}
                  >
                    Coverdell Education Savings Accounts (ESAs){' '}
                  </Link>
                  and 529 plans.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 0,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1099-Q
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-q-img.webp" />
                    <source srcSet="/assets/images/1099-q-img.png" />
                    <img
                      src="/assets/images/1099-q-img.png"
                      alt="Form 1099-Q, Payments From Qualified Education Programs "
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1099-DA
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1099-DA,{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-da/"
                    sx={{ color: '#0202ff' }}
                  >
                    Digital Asset
                  </Link>{' '}
                  Proceeds From Broker Transactions – Report proceeds from sales
                  or exchanges of digital assets.{' '}
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 0,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        // href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                        href="/contact-us/"
                      >
                        E-file Form 1099-DA
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1099-da-img.webp" />
                    <source srcSet="/assets/images/1099-da-img.png" />
                    <img
                      src="/assets/images/1099-da-img.png"
                      alt="Form 1099-Q, Payments From Qualified Education Programs "
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form W-2G
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Certain Gambling Winnings –{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w-2g/"
                    sx={{ color: '#0202ff' }}
                  >
                    Report gambling winnings
                  </Link>{' '}
                  and any taxes withheld.{' '}
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 0,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form W-2G
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/form-w-2g-img.webp" />
                    <source srcSet="/assets/images/form-w-2g-img.png" />
                    <img
                      src="/assets/images/form-w-2g-img.png"
                      alt="Certain Gambling Winnings – Report gambling winnings and any taxes withheld."
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1097-BTC
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1097-BTC, Bond Tax credit to report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1097-btc/"
                    sx={{ color: '#0202ff' }}
                  >
                    tax credit distributed
                  </Link>{' '}
                  to each recipient by bond issuers or agent.{' '}
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 0,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1097-BTC
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mt: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/form-1097-btc-img.webp" />
                    <source srcSet="/assets/images/form-1097-btc-img.png" />
                    <img
                      src="/assets/images/form-1097-btc-img.png"
                      alt="Certain Gambling Winnings – Report gambling winnings and any taxes withheld."
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 5498
                </Typography>

                <Typography variant="body1" sx={{ mb: 7, minHeight: 104 }}>
                  Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRA contributions
                  </Link>
                  , rollovers, conversions, and required minimum distributions
                  (RMDs) to the IRS and account holders.{' '}
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 0,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-File Form 5498
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mt: 6,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/5498-img.webp" />
                    <source srcSet="/assets/images/5498-img.png" />
                    <img
                      src="/assets/images/5498-img.png"
                      alt="Report IRA contributions, rollovers, conversions, "
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 5498-ESA
                </Typography>

                <Typography variant="body1" sx={{ mb: 7, minHeight: 104 }}>
                  Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498-esa/"
                    sx={{ color: '#0202ff' }}
                  >
                    Coverdell ESA
                  </Link>{' '}
                  Contribution Information to the IRS and account
                  beneficiaries.{' '}
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 0,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 5498-ESA
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mt: 6,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/5498-esa-img.webp" />
                    <source srcSet="/assets/images/5498-esa-img.png" />
                    <img
                      src="/assets/images/5498-esa-img.png"
                      alt="Report Coverdell ESA Contribution Information to the IRS and account beneficiaries. "
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 5498-SA
                </Typography>

                <Typography variant="body1" sx={{ mb: 7, minHeight: 104 }}>
                  Report contributions to an{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498-sa/"
                    sx={{ color: '#0202ff' }}
                  >
                    HSA, Archer MSA, or Medicare Advantage MSA
                  </Link>{' '}
                  to the IRS and account holders.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 0,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 5498-SA
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mt: 6,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/5498-sa-img.webp" />
                    <source srcSet="/assets/images/5498-sa-img.png" />
                    <img
                      src="/assets/images/5498-sa-img.png"
                      alt="Report contributions to an HSA, Archer MSA, or Medicare Advantage MSA to the IRS and account holders."
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 3921
                </Typography>

                <Typography variant="body1" sx={{ mb: 7, minHeight: 104 }}>
                  Exercise of an Incentive Stock Option Under Section 422(b) -
                  Report on the exercise of an{' '}
                  <Link
                    href="/information-returns/efile-irs-form-3921/"
                    sx={{ color: '#0202ff' }}
                  >
                    incentive stock option
                  </Link>{' '}
                  and any related adjustments.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 0,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-File Form 3921
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mt: 6,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/3921-img.webp" />
                    <source srcSet="/assets/images/3921-img.png" />
                    <img
                      src="/assets/images/3921-img.png"
                      alt="Exercise of an Incentive Stock Option Under Section 422(b)"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 3922
                </Typography>

                <Typography variant="body1" sx={{ mb: 7, minHeight: 104 }}>
                  Report the transfer of stock acquired through an{' '}
                  <Link
                    href="/information-returns/efile-irs-form-3922/"
                    sx={{ color: '#0202ff' }}
                  >
                    employee stock purchase plan
                  </Link>{' '}
                  under Section 423(c) to the IRS and employees.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 0,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-File Form 3922
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mt: 6,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/3922-img.webp" />
                    <source srcSet="/assets/images/3922-img.png" />
                    <img
                      src="/assets/images/3922-img.png"
                      alt="Report the transfer of stock acquired through an employee stock purchase plan "
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1098
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1098, Mortgage Interest Statement Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098/"
                    sx={{ color: '#0202ff' }}
                  >
                    mortgage interest received{' '}
                  </Link>
                  from an individual, including a sole proprietor.
                </Typography>
                <Box sx={{ mb: 6 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 7,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1098
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1098-img.webp" />
                    <source srcSet="/assets/images/1098-img.png" />
                    <img
                      src="/assets/images/1098-img.png"
                      alt="Form 1098, Mortgage Interest Statement"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1098-C
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    contributions of motor vehicles, boats, and airplanes{' '}
                  </Link>
                  to qualified charitable organizations to the IRS and donors.
                </Typography>
                <Box sx={{ mb: 6 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 7,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1098-C
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mt: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1098c-img.webp" />
                    <source srcSet="/assets/images/1098c-img.png" />
                    <img
                      src="/assets/images/1098c-img.png"
                      alt="Form 1098, Mortgage Interest Statement"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1098-E
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1098-E, Student Loan Interest Statement Report{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-e/"
                    sx={{ color: '#0202ff' }}
                  >
                    student loan interest{' '}
                  </Link>
                  of $600 or more received from an individual.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1098-E
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1098-e-img.webp" />
                    <source srcSet="/assets/images/1098-e-img.png" />
                    <img
                      src="/assets/images/1098-e-img.png"
                      alt="Form 1098-E, Student Loan Interest Statement"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1098-F
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Reports{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-f/"
                    sx={{ color: '#0202ff' }}
                  >
                    fines, penalties, and other amounts{' '}
                  </Link>{' '}
                  paid to government entities for legal violations for taxpayers{' '}
                  paying $50,000 or more.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1098-F
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1098f-img.webp" />
                    <source srcSet="/assets/images/1098f-img.png" />
                    <img
                      src="/assets/images/1098f-img.png"
                      alt="Reports fines, penalties, and other amounts paid to government entities for legal violations for taxpayers paying $50,000 or more."
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1098-T
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Form 1098-T, Tuition Statement Report all{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    payments received{' '}
                  </Link>
                  from and refunds made to each enrolled student.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1098-T
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1098-t-img.webp" />
                    <source srcSet="/assets/images/1098-t-img.png" />
                    <img
                      src="/assets/images/1098-t-img.png"
                      alt="Form 1098-T, Tuition Statement"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1098-Q
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Report the payments from a{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-q/"
                    sx={{ color: '#0202ff' }}
                  >
                    qualifying longevity annuity contract{' '}
                  </Link>
                  (QLAC) to the IRS and the beneficiary.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1098-Q
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1098q-img.webp" />
                    <source srcSet="/assets/images/1098q-img.png" />
                    <img
                      src="/assets/images/1098q-img.png"
                      alt="qualifying longevity annuity contract"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1095-B
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Report{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095b/"
                    sx={{ color: '#0202ff' }}
                  >
                    Health Coverage{' '}
                  </Link>
                  to the IRS and responsible individual to stay IRS compliant
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1095-B
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1095-b-img.webp" />
                    <source srcSet="/assets/images/1095-b-img.png" />
                    <img
                      src="/assets/images/1095-b-img.png"
                      alt="Report Health Coverage to the IRS and responsible individual to stay IRS compliant"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 2,
                  height: '100%',
                  transition: 'all 0.3s ease 0s',
                  borderWidth: 2,
                  '&:hover, &:focus, &:active': { borderColor: '#989898' },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, mb: 1 }}
                >
                  Form 1095-C
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, minHeight: 104 }}>
                  Report health insurance coverage under{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#0202ff' }}
                  >
                    Employer-Provided Health Insurance Offer and Coverage{' '}
                  </Link>
                  to IRS and employees.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'column',
                      md: 'column',
                    }}
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                      mt: 2,
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      textAlign: { xs: 'center', md: 'left' },
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      component="div"
                    >
                      As low as&nbsp;
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 32 },
                          fontWeight: 700,
                          color: '#4269e2',
                          fontStyle: 'normal',
                        }}
                      >
                        $0.59
                      </Typography>
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        sx={{
                          textAlign: 'center',
                          textTransform: 'none',
                          borderRadius: 4,
                          boxShadow: 'none',
                          px: 3,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': {
                            transform: 'scale(1.1)',
                            boxShadow: 'none',
                          },
                        }}
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
                      >
                        E-file Form 1095-C
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& img': {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      width: '100%',
                      height: '100%',
                      maxWidth: 320,
                      maxHeight: 293,
                    },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/1095-c-img.webp" />
                    <source srcSet="/assets/images/1095-c-img.png" />
                    <img
                      src="/assets/images/1095-c-img.png"
                      alt="Report health insurance coverage under"
                      loading="lazy"
                      width={320}
                      height={293}
                    />
                  </picture>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          sx={{ mt: { xs: 2, sm: 6, md: 8 } }}
          className="MuiGrid-root-width"
        >
          <Grid size={12} textAlign="center">
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Effortless E-filing for Information Returns
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 3,
                textAlign: 'center',
              }}
            >
              Follow the steps below to e-file your information returns with the
              IRS.
            </Typography>
          </Grid>
        </Grid>
      </Container>
      <Container maxWidth="xl" sx={{ mb: 5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
          sx={{ justifyContent: 'center', alignItems: 'center' }}
        >
          <Box
            component="div"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              mt: { xs: -3, md: -5, lg: -8 },
            }}
          >
            <picture>
              <source
                srcSet="/assets/images/information-steps-img.webp"
                className="img-responsive"
                width={915}
                height={790}
              />
              <source
                srcSet="/assets/images/information-steps-img.png"
                className="img-responsive"
                width={915}
                height={790}
              />
              <img
                src="/assets/images/information-steps-img.png"
                alt="Effortless E-filing for Information Returns"
                className="img-responsive"
                loading="lazy"
                width={915}
                height={790}
              />
            </picture>
          </Box>

          <Box
            sx={{
              width: '100%',
            }}
          >
            <Timeline
              sx={{
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0,
                },
                pl: 0,
                ml: { xs: 0, sm: 0, md: -2 },
              }}
            >
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      my: -1,
                      mb: -1.7,
                    }}
                  >
                    <picture>
                      <source
                        srcSet="/assets/images/step-1-icon.webp"
                        width={39}
                        height={39}
                      />
                      <source
                        srcSet="/assets/images/step-1-icon.png"
                        width={39}
                        height={39}
                      />
                      <img
                        src="/assets/images/step-1-icon.png"
                        alt="Select the form you want to file"
                        loading="lazy"
                        width={39}
                        height={39}
                      />
                    </picture>
                  </TimelineDot>
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ mb: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                      fontWeight: 600,
                      mt: 0,
                      mb: 1,
                      color: '#36324a',
                    }}
                  >
                    Select the form you want to file
                  </Typography>
                </TimelineContent>
              </TimelineItem>
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      m: 0,
                      mt: -1,
                      mb: -1.7,
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/step-2-icon.webp" />
                      <source srcSet="/assets/images/step-2-icon.png" />
                      <img
                        src="/assets/images/step-2-icon.png"
                        alt="Fill out the form information"
                        loading="lazy"
                        width={39}
                        height={39}
                      />
                    </picture>
                  </TimelineDot>
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ mb: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                      fontWeight: 600,
                      mt: 0,
                      mb: 1,
                      color: '#36324a',
                    }}
                  >
                    Fill out the form information
                  </Typography>
                </TimelineContent>
              </TimelineItem>
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      mt: -1,
                      mb: -1.7,
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/step-3-icon.webp" />
                      <source srcSet="/assets/images/step-3-icon.png" />
                      <img
                        src="/assets/images/step-3-icon.png"
                        alt="Review and transmit the form to the IRS"
                        loading="lazy"
                        width={39}
                        height={39}
                      />
                    </picture>
                  </TimelineDot>
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                      fontWeight: 600,
                      mt: 0,
                      mb: 1,
                      color: '#36324a',
                    }}
                  >
                    Review and transmit the form to the IRS
                  </Typography>
                </TimelineContent>
              </TimelineItem>
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      mt: -1,
                      mb: -1.7,
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/step-4-icon.webp" />
                      <source srcSet="/assets/images/step-4-icon.png" />
                      <img
                        src="/assets/images/step-4-icon.png"
                        alt="Send the employee/contractor copy"
                        loading="lazy"
                        width={39}
                        height={39}
                      />
                    </picture>
                  </TimelineDot>
                </TimelineSeparator>
                <TimelineContent>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                      fontWeight: 600,
                      mt: 0,
                      mb: 1,
                      color: '#36324a',
                    }}
                  >
                    Send the employee/contractor a copy
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            </Timeline>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 21, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Ready to e-file with TaxZerone?
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: { xs: 2, sm: 4 },
                  mb: { xs: 2, sm: 2, md: 5 },
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
              >
                Start Filing
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Why Choose TaxZerone to E-file Information Returns?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                TaxZerone simplifies information return filing with these
                amazing features
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} m={2} mt={4} pb={{ xs: 0, sm: 4, md: 7 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/form-based-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/form-based-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/form-based-icon.png"
                      alt="Form-based filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Form-based filing
                </Typography>
                <Typography variant="body1">
                  Form-based filing ensures you complete all the necessary
                  fields in less time. Whether it&apos;s Form W-2, Form
                  1099-MISC, or Form 1099-NEC, we&apos;ve got you covered.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/irs-rule-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/irs-rule-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/irs-rule-icon.png"
                      alt="IRS form validations"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  IRS form validations
                </Typography>
                <Typography variant="body1">
                  Built-in IRS form validations ensure that your submissions
                  meet the necessary criteria for a seamless and accurate filing
                  process.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/time-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/time-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/time-icon.png"
                      alt="E-file in minutes"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  E-file in minutes
                </Typography>
                <Typography variant="body1">
                  Complete the entire process in a matter of minutes. Save time
                  and effort, leaving you with more resources to focus on what
                  matters most - your business.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/guided-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/guided-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/guided-icon.png"
                      alt="Guided filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Guided filing
                </Typography>
                <Typography variant="body1">
                  Guided filing walks you through each step and makes the
                  process simple and stress-free. We&apos;ll ensure you fill out
                  the forms correctly every time.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/secure-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/secure-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/secure-icon.png"
                      alt="Quick and secure e-filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Quick and secure e-filing
                </Typography>
                <Typography variant="body1">
                  Speed and security are at the heart of our e-filing service.
                  You can e-file your returns quickly and securely, with your
                  data protected every step of the way.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/email-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/email-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/email-icon.png"
                      alt="Email return copies"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Email return copies
                </Typography>
                <Typography variant="body1">
                  Email copies of the returns to recipients, so you can ensure
                  they have the information they need without delay.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          Ready to E-file IRS Forms with the IRS?
        </Typography>

        <Typography
          variant="h3"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          Make the e-filing process simple and hassle-free by clicking the
          button below.
        </Typography>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=information`}
          >
            Start Filing
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is Form W-2?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Form W-2 is used by employers to report wages and tax
                    withholdings for their employees. It&apos;s a crucial form
                    for both employers and employees during the tax season, as
                    it helps in the accurate reporting of income and taxes paid.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What is Form 1099-MISC?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Form 1099-MISC reports various types of miscellaneous
                    income. It is typically used to report payments made to
                    non-employees, such as independent contractors, as well as
                    other forms of income like rents, royalties, and other
                    miscellaneous payments. Businesses must file this form for
                    payments exceeding $600 during the tax year.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What is Form 1099-NEC?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Form 1099-NEC, non-employee compensation, is used to report
                    payments made to independent contractors, freelancers, or
                    self-employed individuals. It&apos;s separate from the
                    traditional Form 1099-MISC and simplifies the reporting of
                    non-employee compensation for businesses.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Index;
